import { useState, useCallback, useRef, useEffect } from "react";

import { Container } from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";

import FilterCard from "components/_refactor/filter-card";
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import UtilsFncs from "utils/UtilsFncs";

import Filters from "./components/filters";
import TablePrePedidos from "./components/table";
import PrePedidoDetailDrawer from "./components/pre-pedido-detail-drawer";
import "./pre-pedidos.scss";

const EMPTY_FILTERS = {
  vitrineName: "",
  cnpj: "",
  dataPrePedidoInicio: null,
  dataPrePedidoFim: null,
};

export default function PrePedidosVitrineB2b() {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDetalhe, setSelectedDetalhe] = useState(null);
  const [pedidosList, setPedidosList] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    totalSize: 0,
  });

  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);

  filtersRef.current = filters;
  paginationRef.current = pagination;

  const snackbar = useSnackbar();

  const fetchPedidos = useCallback(async (pageOverride, pageSizeOverride) => {
    setIsLoading(true);

    const currentFilters = filtersRef.current;
    const page = pageOverride ?? paginationRef.current.page ?? 0;
    const pageSize = pageSizeOverride ?? paginationRef.current.pageSize ?? 25;

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });

    if (currentFilters.vitrineName?.trim()) {
      params.set("nome", currentFilters.vitrineName.trim());
    }
    if (currentFilters.cnpj?.trim()) {
      params.set("cnpj", currentFilters.cnpj.trim());
    }
    if (currentFilters.dataPrePedidoInicio) {
      params.set(
        "dataPrePedidoInicio",
        UtilsFncs.ConvertDateFormat(currentFilters.dataPrePedidoInicio, "-", "-"),
      );
    }
    if (currentFilters.dataPrePedidoFim) {
      params.set(
        "dataPrePedidoFim",
        UtilsFncs.ConvertDateFormat(currentFilters.dataPrePedidoFim, "-", "-"),
      );
    }

    try {
      const response = await Api.get(
        "virtual-showcase-b2b-list-pre-pedidos",
        "showcaseB2B",
        `?${params.toString()}`,
        true,
      );
      const result = response?.Result?.Pedidos || response?.Result?.pedidos;
      setPagination({
        totalSize: response?.Result?.TotalRegistro || 0,
        page: response?.Result?.Pagina ?? page,
        pageSize,
        currentPage: response?.Result?.Pagina ?? page,
        currentSizePerPage: pageSize,
      });
      setPedidosList(Array.isArray(result) ? result : []);
    } catch {
      snackbar.showMessage("Falha ao buscar pré-pedidos. Tente novamente.", {
        severity: "error",
      });
      setPedidosList([]);
    } finally {
      setIsLoading(false);
    }
  }, [snackbar]);

  const handleSearch = () => {
    setHasSearched(true);
    setPagination((prev) => ({ ...prev, page: 0 }));
    fetchPedidos(0);
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      page: page || 0,
      pageSize: pageSize || 25,
    }));
    if (hasSearched) {
      fetchPedidos(page || 0, pageSize || 25);
    }
  };

  const handleViewDetails = useCallback(
    async (idPedido) => {
      setLoadingDetails(true);
      setDetailOpen(true);
      setSelectedDetalhe(null);

      try {
        const response = await Api.get(
          "virtual-showcase-b2b-pre-pedido-detalhe",
          "showcaseB2B",
          `?idVitrineVirtualB2bPedido=${idPedido}`,
          true,
        );
        setSelectedDetalhe(response?.Result || null);
      } catch {
        snackbar.showMessage("Falha ao carregar detalhes do pré-pedido.", {
          severity: "error",
        });
        setDetailOpen(false);
      } finally {
        setLoadingDetails(false);
      }
    },
    [snackbar],
  );

  useEffect(() => {
    setHasSearched(true);
    fetchPedidos(0);
  }, [fetchPedidos]);

  return (
    <>
      <HeaderClientes
        section="Pré-pedidos Vitrine B2B"
        subsection="Pré-Pedido"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <FilterCard
          handleSearch={handleSearch}
          disabled={isLoading}
          searching={isLoading}
        >
          <Filters
            isLoading={isLoading}
            filters={filters}
            setFilters={setFilters}
          />
        </FilterCard>
        <TablePrePedidos
          data={pedidosList}
          loading={isLoading}
          totalSize={pagination.totalSize}
          currentPage={pagination.page}
          currentSizePerPage={pagination.pageSize}
          onPageChange={handlePageChange}
          loadingDetails={loadingDetails}
          handleViewDetails={handleViewDetails}
        />
      </Container>

      <PrePedidoDetailDrawer
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedDetalhe(null);
        }}
        detalhe={selectedDetalhe}
        loading={loadingDetails}
      />
    </>
  );
}
